import React, { useEffect, useState } from 'react'
import DefaultLayout from '../reusableComponents/defaultLayout'
import ReactPaginate from 'react-paginate';
import { ThreeDots } from 'react-loader-spinner';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import AccountHeader, { subContractorTabs } from '../components/account/AccountHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GavelIcon from '@mui/icons-material/Gavel';
import { API_BASE_URL } from '../config/api';

export default function FindProject() {
    const [projects, setProjects] = useState([]);
    const [count, setCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedScope, setSelectedScope] = useState('');
    const [location, setLocation] = useState('');
    const [zoningType, setZoningType] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);

    const navigate = useNavigate();
    const userRole = useSelector((state) => state?.userProfileSlice?.userData?.data?.role);
    const isSubContractor = userRole === 'sub_contractor';

    const states = [
        "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DE", "DC", "FL",
        "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
        "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
        "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
        "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
        "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK"
    ];

    const scopeOptions = [
        { value: 'site_preparation', label: 'Site Preparation' },
        { value: 'concrete', label: 'Concrete' },
        { value: 'structural_and_framing', label: 'Structural & Framing' },
        { value: 'roofing_siding_and_sheet_metal_work', label: 'Roofing & Sheet Metal' },
        { value: 'plumbing', label: 'Plumbing' },
        { value: 'hvac', label: 'HVAC' },
        { value: 'electrical', label: 'Electrical' },
        { value: 'carpentry', label: 'Carpentry' },
        { value: 'drywall', label: 'Drywall' },
        { value: 'painting_and_paper_hanging', label: 'Painting & Wallpaper' },
        { value: 'other', label: 'Other' }
    ];

    const serviceOptions = [
        { value: 'commercial', label: 'Commercial' },
        { value: 'residential', label: 'Residential' },
        { value: 'federal', label: 'Federal' },
        { value: 'road_construction_and_industrial', label: 'Road & Industrial' },
        { value: 'other', label: 'Other' }
    ];

    const handleDateChange = (date) => {
        if (date) {
            const formattedDate = moment(date.$d).format('YYYY-MM-DD');
            setSelectedDate(formattedDate);
        } else {
            setSelectedDate(null);
        }
    };

    const clearFilters = () => {
        setSelectedScope('');
        setLocation('');
        setZoningType('');
        setSelectedDate(null);
    };

    const hasActiveFilters = selectedScope || location || zoningType || selectedDate;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const url = API_BASE_URL;
                const token = localStorage.getItem('authToken');
                const queryString = `?page_num_start=1&page_size=10&identify_scope=${selectedScope}&services=${zoningType}&licensed_states_of_work=${location}&timeline=${selectedDate || ''}`;

                const requestOptions = {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                };

                const response = await fetch(`${url}/projects_list${queryString}`, requestOptions);
                const data = await response.json();
                
                if (data.data.projects.length > 0) {
                    setCount(data.data.projectsCount / 10);
                    setPageCount(data.data.projectsCount);
                    setProjects(data.data.projects);
                } else {
                    setCount(0);
                    setPageCount(0);
                    setProjects([]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [zoningType, selectedDate, selectedScope, location]);

    const fetchPaginatedData = async (currentPage) => {
        const url = API_BASE_URL;
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        const res = await fetch(`${url}/projects_list?page_num_start=${currentPage}&page_size=10`, requestOptions);
        const data = await res.json();
        setLoading(false);
        return data;
    };

    const handlePageClick = async (data) => {
        const currentPage = data.selected + 1;
        const dataFromServer = await fetchPaginatedData(currentPage);
        setProjects(dataFromServer?.data?.projects);
    };

    return (
        <DefaultLayout>
            <AccountHeader tabs={isSubContractor ? subContractorTabs : undefined} />
            
            <div className="profile_page">
                <div className="container">
                    <button className="back_link" onClick={() => navigate(-1)}>
                        <ArrowBackIcon fontSize="small" /> Back
                    </button>

                    {/* Search Filters Section */}
                    <div className="profile_section">
                        <div className="section_header">
                            <div className="section_header_row">
                                <div>
                                    <h3>Find Projects</h3>
                                    <p>Search and filter available projects to bid on</p>
                                </div>
                                {hasActiveFilters && (
                                    <button className="clear_filters_btn" onClick={clearFilters}>
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="section_content">
                            <div className="filter_grid">
                                <div className="filter_item">
                                    <label>Scope of Work</label>
                                    <select value={selectedScope} onChange={(e) => setSelectedScope(e.target.value)} className="form-control">
                                        <option value="">All Scopes</option>
                                        {scopeOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter_item">
                                    <label>Location</label>
                                    <select value={location} onChange={(e) => setLocation(e.target.value)} className="form-control">
                                        <option value="">All States</option>
                                        {states.map((state, idx) => (
                                            <option key={idx} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter_item">
                                    <label>Service Type</label>
                                    <select value={zoningType} onChange={(e) => setZoningType(e.target.value)} className="form-control">
                                        <option value="">All Services</option>
                                        {serviceOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter_item">
                                    <label>Start Date</label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker 
                                            value={selectedDate} 
                                            onChange={handleDateChange}
                                            slotProps={{ 
                                                textField: { 
                                                    fullWidth: true, 
                                                    placeholder: 'Any Date',
                                                    size: 'small'
                                                } 
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Section */}
                    <div className="profile_section">
                        <div className="section_header">
                            <h3>Available Projects</h3>
                            <p>{loading ? 'Loading...' : `${pageCount} project${pageCount !== 1 ? 's' : ''} found`}</p>
                        </div>
                        <div className="section_content">
                            {loading ? (
                                <div className="loading_state">
                                    <ThreeDots
                                        height="60"
                                        width="80"
                                        radius="9"
                                        color="#B1903F"
                                        ariaLabel="three-dots-loading"
                                        visible={true}
                                    />
                                    <p>Loading projects...</p>
                                </div>
                            ) : projects.length === 0 ? (
                                <div className="empty_state">
                                    <SearchIcon className="empty_icon" />
                                    <h4>No Projects Found</h4>
                                    <p>Try adjusting your filters or check back later for new opportunities.</p>
                                </div>
                            ) : (
                                <div className="projects_list">
                                    {projects.map((project, index) => (
                                        <div key={index} className="project_card">
                                            <div className="project_card_header">
                                                <div className="project_info" onClick={() => navigate(`/project-details/${project.id}`)}>
                                                    <h4>{project.project_name}</h4>
                                                    <span className="project_date">{moment(project.project_start_date).format('MMM DD, YYYY')}</span>
                                                </div>
                                                <div className="project_actions">
                                                    <Link to={`/submitproposal/${project.id}`} className="bid_button">
                                                        <GavelIcon fontSize="small" />
                                                        Bid Now
                                                    </Link>
                                                    <span className="bid_count">
                                                        <VisibilityIcon fontSize="small" />
                                                        {project.bids_count}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="project_description">
                                                <strong>Description:</strong> {project.project_description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {pageCount > 10 && (
                                <div className="pagination_wrapper">
                                    <ReactPaginate
                                        previousLabel={"Prev"}
                                        nextLabel={"Next"}
                                        breakLabel={"..."}
                                        pageCount={count}
                                        marginPagesDisplayed={2}
                                        pageRangeDisplayed={3}
                                        onPageChange={handlePageClick}
                                        containerClassName={"pagination"}
                                        pageClassName={"page-item"}
                                        pageLinkClassName={"item-link"}
                                        previousClassName={"page-item"}
                                        previousLinkClassName={"item-link"}
                                        nextClassName={"page-item"}
                                        nextLinkClassName={"item-link"}
                                        breakClassName={"page-item"}
                                        breakLinkClassName={"item-link"}
                                        activeClassName={"active"}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DefaultLayout>
    );
}
